/**
 * Fiche de révision d'un pack, gabarit DENSE : un bloc compact par carte.
 *
 * Par carte : numéro, titre, état de maîtrise, « quand l'utiliser », la règle (tableaux et
 * formules compris), l'exemple réduit à « énoncé → résultat », et le PIÈGE. Une carte « à revoir »
 * est mise en avant (filet dans la marge + pastille) : c'est ce que l'élève vient chercher.
 *
 * Une carte verrouillée arrive sans contenu (paywall serveur, RPC 0723) : on affiche son titre et
 * sa place dans le pack, ce qui fait la valeur du pack complet sans rien livrer.
 *
 * Composant de PRÉSENTATION pur (aucune requête) : il se monte sur un banc avec des données
 * simulées pour la validation à l'écran.
 */

import { AlertTriangle, Lock, Target } from 'lucide-react';
import { MarkdownMath, MathText, cn } from '@jeraisonne/ui';
import type { CardSection } from '@jeraisonne/db/cards';
import {
  MASTERY_LABELS,
  masteryStateOf,
  type MasteryState,
  type RevisionSheet,
  type RevisionSheetCard,
} from '@/lib/cards/pack-revision-sheet';
import './revision-sheet.css';

const STATE_CHIP: Record<MasteryState, string> = {
  mastered: 'border-xp-200 bg-xp-50 text-xp-700',
  fragile: 'border-accent-300 bg-accent-50 text-accent-600',
  untouched: 'border-ink-200 bg-ink-50 text-ink-500',
};

function StateChip({ state }: { state: MasteryState }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
        STATE_CHIP[state],
      )}
    >
      {MASTERY_LABELS[state]}
    </span>
  );
}

/** Tableaux et formules du contenu structuré : ce qui porte l'essentiel d'une règle. */
function DenseSection({ section }: { section: CardSection }) {
  switch (section.kind) {
    case 'table':
      return (
        <table className="jr-fiche-table text-ink-800 w-full border-collapse text-[13px]">
          <thead>
            <tr>
              {section.headers.map((h, i) => (
                <th key={i} className="border-ink-200 border px-1.5 py-0.5 text-left font-semibold">
                  <MarkdownMath>{h}</MarkdownMath>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c} className="border-ink-200 border px-1.5 py-0.5">
                    <MarkdownMath>{cell}</MarkdownMath>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    case 'formula':
      return <MarkdownMath>{`$$${section.latex}$$`}</MarkdownMath>;
    case 'steps':
      return (
        <ol className="text-ink-800 list-decimal space-y-0.5 pl-5 text-[13px]">
          {section.items.map((item, i) => (
            <li key={i}>
              <MarkdownMath>{item}</MarkdownMath>
            </li>
          ))}
        </ol>
      );
    default:
      return null;
  }
}

/** Exemple en une ligne : « énoncé → résultat ». Les étapes restent sur la carte. */
function DenseExample({ sections }: { sections: readonly CardSection[] }) {
  const ex = sections.find((s) => s.kind === 'example');
  if (!ex || ex.kind !== 'example') return null;
  if (!ex.statement && !ex.result) return null;
  return (
    <p className="text-ink-600 text-[13px]">
      <span className="font-semibold">Ex. </span>
      <MathText>{ex.statement}</MathText>
      {ex.result ? (
        <>
          {' → '}
          <span className="text-ink-900 font-semibold">
            <MathText>{ex.result}</MathText>
          </span>
        </>
      ) : null}
    </p>
  );
}

function LockedCard({ card, index }: { card: RevisionSheetCard; index: number }) {
  return (
    <article
      className="jr-fiche-card border-ink-200 bg-ink-50/60 flex items-center gap-2 rounded-xl border border-dashed px-3 py-2"
      data-state="locked"
    >
      <span className="text-ink-400 w-5 shrink-0 text-right text-xs font-bold tabular-nums">
        {index}
      </span>
      <Lock className="text-ink-400 h-3.5 w-3.5 shrink-0" aria-hidden />
      <h3 className="text-ink-500 flex-1 text-sm font-semibold">
        <MathText>{card.title}</MathText>
      </h3>
      <span className="text-ink-400 shrink-0 text-[11px]">Carte non débloquée</span>
    </article>
  );
}

function SheetCard({ card, index }: { card: RevisionSheetCard; index: number }) {
  if (card.locked) return <LockedCard card={card} index={index} />;
  const state = masteryStateOf(card);
  const tables = card.sections.filter(
    (s) => s.kind === 'table' || s.kind === 'formula' || s.kind === 'steps',
  );
  return (
    <article
      className={cn(
        'jr-fiche-card border-ink-200 space-y-1.5 rounded-xl border bg-white px-3 py-2.5',
        state === 'fragile' && 'border-l-accent-400 border-l-4',
      )}
      data-state={state}
    >
      <div className="flex items-start gap-2">
        <span className="text-ink-400 w-5 shrink-0 text-right text-xs font-bold tabular-nums">
          {index}
        </span>
        <h3 className="font-display text-ink-900 flex-1 text-sm font-extrabold">
          <MathText>{card.title}</MathText>
        </h3>
        <StateChip state={state} />
      </div>

      <div className="space-y-1.5 pl-7">
        {card.when_to_use && (
          <div className="text-ink-600 flex items-start gap-1.5 text-[13px] italic">
            <Target className="text-primary-500 mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <MarkdownMath>{card.when_to_use}</MarkdownMath>
          </div>
        )}

        {card.rule && <MarkdownMath className="text-ink-800 text-[13px]">{card.rule}</MarkdownMath>}

        {tables.map((s, i) => (
          <DenseSection key={i} section={s} />
        ))}

        <DenseExample sections={card.sections} />

        {card.pitfalls && (
          <div className="jr-fiche-pitfall flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-[13px]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
            <div className="text-ink-800">
              <span className="font-semibold">Piège. </span>
              {/* `MarkdownMath` rend un bloc : sans ça, le texte passe à la ligne après « Piège. ». */}
              <MarkdownMath className="inline [&_p]:inline">{card.pitfalls}</MarkdownMath>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export function RevisionSheetView({ sheet }: { sheet: RevisionSheet }) {
  const generated = new Date(sheet.generated_at);
  const stamp = Number.isNaN(generated.getTime())
    ? ''
    : generated.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="jr-fiche space-y-3">
      <header className="space-y-0.5">
        <h2 className="font-display text-ink-900 text-lg font-extrabold">
          Fiche de révision · {sheet.pack.title}
        </h2>
        <p className="text-ink-500 text-xs">
          {sheet.card_count} carte{sheet.card_count > 1 ? 's' : ''} · {sheet.unlocked_count}{' '}
          débloquée{sheet.unlocked_count > 1 ? 's' : ''}
          {stamp ? ` · ${stamp}` : ''}
        </p>
      </header>

      <div className="space-y-2">
        {sheet.cards.map((card, i) => (
          <SheetCard key={card.id} card={card} index={i + 1} />
        ))}
      </div>

      {/* Caché à l'écran, répété sur chaque page imprimée : un PDF qui circule reste nominatif. */}
      <p className="jr-fiche-footer hidden">
        Fiche de révision « {sheet.pack.title} » — {sheet.student.display_name ?? 'élève'}
        {stamp ? ` · ${stamp}` : ''} · jeraisonne.fr
      </p>
    </div>
  );
}
